import React, { type EffectCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';
import { Input } from '@core-ui/Components/ui/input';
import { cn } from '@core-ui-lib/utils';
import { Pause, Play, Waves } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  initialTitle: string;
  audioUrl?: string;
  loading?: boolean;
  onCancel: () => void;
  onSave: (title: string) => void | Promise<void>;
  disableCloseOnLoading?: boolean;
}

function MiniWave ({
  progress = 0,
  animated = false,
}: {
  progress?: number;
  animated?: boolean;
}): React.JSX.Element {
  const bars = 42;

  const heights = useMemo(
    () => Array.from({ length: bars }).map((_, i) => 8 + ((i * 17) % 24)),
    [],
  );

  return (
    <div className="flex items-center justify-center gap-[3px] h-16">
      {heights.map((h, i) => {
        const active = i / bars <= progress;
        return (
          <span
            key={i}
            className={cn(
              'w-[3px] rounded-sm transition-all',
              active ? 'bg-primary' : 'bg-primary/25',
              animated ? 'opacity-100' : 'opacity-90',
            )}
            style={{ height: h }}
          />
        );
      })}
    </div>
  );
}

export default function RenameVoicemailDialog ({
  open,
  initialTitle,
  audioUrl,
  loading = false,
  onCancel,
  onSave,
  disableCloseOnLoading = true,
}: Props): React.JSX.Element {
  const { t } = useTranslation();

  const [title, setTitle] = useState(initialTitle);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (open) {
      setTitle(initialTitle);
    }
  }, [open, initialTitle]);

  useEffect((): ReturnType<EffectCallback> => {
    if (!open) {
      setPlaying(false);
      setProgress(0);
      audioRef.current?.pause();
      return;
    }

    if (audioUrl != null && audioUrl.trim() !== '') {
      const el = (audioRef.current ??= new Audio());
      el.src = audioUrl;
      el.currentTime = 0;

      const onTime = (): void => {
        if (Number.isFinite(el.duration) && el.duration > 0) {
          setProgress(el.currentTime / el.duration);
        } else {
          setProgress(0);
        }
      };

      const onEnd = (): void => {
        setPlaying(false);
        setProgress(0);
      };

      el.addEventListener('timeupdate', onTime);
      el.addEventListener('ended', onEnd);

      return (): void => {
        el.pause();
        el.removeEventListener('timeupdate', onTime);
        el.removeEventListener('ended', onEnd);
      };
    }

  }, [open, audioUrl]);

  const togglePlay = async (): Promise<void> => {
    if ((audioUrl == null) || audioUrl.trim() === '' || loading) {
      return;
    }

    const el = (audioRef.current ??= new Audio());
    if (el.src !== audioUrl) {
      el.src = audioUrl;
    }

    if (playing) {
      el.pause();
      setPlaying(false);
      return;
    }

    try {
      await el.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
    }
  };

  const handleOpenChange = (isOpen: boolean): void => {
    if (disableCloseOnLoading && loading) {
      return;
    }
    if (!isOpen) {
      onCancel();
    }
  };

  const handleSave = (): void => {
    const clean = title.trim();
    if (clean === '' || loading) {
      return;
    }
    void onSave(clean);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-lg rounded-2xl shadow-lg border border-border bg-card text-foreground p-6"
        onEscapeKeyDown={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
        onPointerDownOutside={(e) => {
          if (disableCloseOnLoading && loading) {
            e.preventDefault();
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !loading) {
            e.preventDefault();
            handleSave();
          }
        }}
      >
        <DialogHeader className="mb-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-primary/20 bg-primary/10 text-primary">
              <Waves className="h-5 w-5" />
            </span>
            <div className="space-y-0.5">
              <DialogTitle className="text-lg font-semibold">
                {t('voice.voicemails.rename.title', 'Rename voicemail')}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground">
                {t('voice.voicemails.rename.subtitle', 'Update the name and preview the audio.')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Title input */}
        <div className="space-y-2 mt-3">
          <label className="text-sm text-muted-foreground">
            {t('voice.voicemails.rename.nameLabel', 'Voicemail name')}
          </label>
          <Input
            data-qa-id="edit-vm-name"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
            }}
            placeholder={t('voice.voicemails.rename.namePh', 'Voicemail title')}
            disabled={loading}
          />
        </div>

        {/* Audio preview */}
        <div className="mt-6 rounded-xl border border-border bg-muted/30 px-4 py-6">
          <MiniWave progress={playing ? progress : 0} animated={playing} />

          <div className="mt-4 flex justify-center">
            <button
              data-qa-id="play-preview-audio"
              type="button"
              onClick={() => {
                void togglePlay();
              }}
              disabled={loading || (audioUrl == null)}
              className={cn(
                'flex h-12 w-12 items-center justify-center rounded-full border transition',
                playing
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-primary hover:bg-muted',
                (loading || (audioUrl == null)) ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer',
              )}
              aria-label={playing ? 'Pause' : 'Play'}
              title={playing ? 'Pause' : 'Play'}
            >
              {playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
            </button>
          </div>

          <p className="mt-3 text-center text-xs text-muted-foreground">
            {(audioUrl != null)
              ? (playing
                ? t('voice.voicemails.rename.playing', 'Playing… click to pause')
                : t('voice.voicemails.rename.tapToPlay', 'Click to preview audio'))
              : t('voice.voicemails.rename.noAudio', 'No audio available for preview')}
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2 mt-6">
          <Button
            data-qa-id="cancel-edit-vm-button"
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
            className="border border-border text-foreground hover:bg-muted"
            autoFocus
          >
            {t('adminConsole.common.cancel', 'Cancel')}
          </Button>

          <Button
            data-qa-id="save-vm-form-button"
            type="button"
            onClick={handleSave}
            disabled={loading || title.trim() === ''}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {loading ? t('settings.settingsTab.saving', 'Saving…') : t('adminConsole.common.save', 'Save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
